/**
 * Web-only FlightRouteGraphic - uses fallback path (no react-native-svg)
 * Avoids ESM/CommonJS issues with react-native-svg in webpack
 */

import React, { useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Animated } from 'react-native';

const ACCENT_CYAN = '#00b8d9';

interface FlightRouteGraphicProps {
  origin: string;
  destination: string;
  visible: boolean;
  size?: number;
}

function AirplaneIcon({ size = 24 }: { size?: number }) {
  return <Text style={[styles.airplaneIcon, { fontSize: size }]}>✈</Text>;
}

function PathLineFallback({ visible }: { visible: boolean }) {
  const opacity = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (visible) {
      Animated.timing(opacity, { toValue: 1, duration: 400, useNativeDriver: true }).start();
    } else {
      opacity.setValue(0);
    }
  }, [visible, opacity]);
  return (
    <Animated.View style={[styles.fallbackPath, { opacity }]}>
      <Text style={styles.fallbackPathText}>—— ✈ ——</Text>
    </Animated.View>
  );
}

export function FlightRouteGraphic({
  origin,
  destination,
  visible,
  size = 24,
}: FlightRouteGraphicProps) {
  const hasRoute = origin.trim().length >= 2 && destination.trim().length >= 2;

  return (
    <View style={styles.container}>
      <View style={styles.routeRow}>
        <View style={styles.endpoint}>
          <AirplaneIcon size={size} />
          <Text style={styles.code} numberOfLines={1}>
            {origin.trim() || '—'}
          </Text>
        </View>
        <View style={styles.pathContainer}>
          <PathLineFallback visible={visible && hasRoute} />
        </View>
        <View style={styles.endpoint}>
          <AirplaneIcon size={size} />
          <Text style={styles.code} numberOfLines={1}>
            {destination.trim() || '—'}
          </Text>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { marginBottom: 12 },
  routeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  endpoint: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minWidth: 60,
  },
  airplaneIcon: { color: ACCENT_CYAN },
  code: { fontSize: 14, fontWeight: '600', color: '#0f172a' },
  pathContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 60,
    paddingHorizontal: 8,
  },
  fallbackPath: { paddingVertical: 4 },
  fallbackPathText: { color: ACCENT_CYAN, fontSize: 14, opacity: 0.8 },
});
